import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { Loader2, ChevronLeft, ChevronRight, Settings, LogOut, LayoutDashboard, Sun, Moon, AlertCircle, RefreshCw, LayoutList, Monitor, Share2, Check, GripVertical, ChevronDown, Star, CalendarDays } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useOrganization } from '../../context/OrganizationContext';
import OrganizationRail, { ORG_RAIL_WIDTH, useShowOrgRail, CurrentOrgCaption } from '../../components/ui/OrganizationRail';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import GanttViewerPage from './GanttViewerPage';
import DocsListPage from '../docs/DocsListPage';
import DocViewPage from '../docs/DocViewPage';
import DocEditorPage from '../docs/DocEditorPage';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// O'zbek digraf harflarini hisobga olib birinchi harfni oladi: Sh, Ch, G', O', Ng
function getInitial(name) {
    if (!name) return '';
    const digraphs = ["Sh", "Ch", "G'", "O'", "Ng", "sh", "ch", "g'", "o'", "ng"];
    for (const d of digraphs) {
        if (name.startsWith(d)) return d.charAt(0).toUpperCase() + d.charAt(1);
    }
    return name.charAt(0).toUpperCase();
}

function DashboardIframe({ sub, token, isDark, lang }) {
    const [loaded, setLoaded] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [iframeError, setIframeError] = useState(null);

    useEffect(() => {
        let mounted = true;
        api.post(`/panel/ticket/${sub.id}`)
            .then(res => {
                if (mounted) setTicket(res.data.ticket);
            })
            .catch(err => {
                if (mounted) setIframeError(err.response?.data?.detail || "Could not fetch ticket");
            });
        return () => { mounted = false; };
    }, [sub.id]);

    const iframeSrc = ticket
        ? `${API_BASE}/panel/iframe/${sub.id}?ticket=${encodeURIComponent(ticket)}&theme=${isDark ? 'dark' : 'light'}&lang=${lang}`
        : null;

    if (iframeError) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-surface">
                <div className="flex flex-col items-center text-error gap-2">
                    <AlertCircle className="w-8 h-8" />
                    <span className="font-semibold text-sm">{iframeError}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col bg-surface overflow-hidden flex-1 relative min-w-0">
            <div className="relative w-full flex-1 bg-background">
                {(!loaded || !ticket) && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 bg-surface">
                        <Loader2 className="w-8 h-8 animate-spin text-brand" />
                    </div>
                )}
                {iframeSrc && (
                    <iframe
                        src={iframeSrc}
                        className="w-full h-full border-0 absolute inset-0"
                        onLoad={() => setLoaded(true)}
                        title={sub.name_ru}
                        allowFullScreen
                    />
                )}
            </div>
        </div>
    );
}

function EmptyState({ title, description, icon: IconComponent, image }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center p-8 text-center max-w-lg mx-auto h-full"
        >
            {image ? (
                <div className="w-64 h-64 mb-8 relative">
                    <div className="absolute inset-0 bg-brand/5 rounded-full blur-3xl animate-pulse" />
                    <img src={image} alt="Empty state" className="w-full h-full object-contain relative z-10 drop-shadow-2xl" />
                </div>
            ) : (
                <div className="w-20 h-20 rounded-3xl bg-surface-2 border border-border flex items-center justify-center mb-6 text-brand/30 shadow-sm">
                    {IconComponent && <IconComponent className="w-10 h-10" />}
                </div>
            )}
            <h2 className="text-2xl font-bold text-primary mb-3">{title}</h2>
            <p className="text-muted leading-relaxed text-sm mb-8">
                {description}
            </p>
        </motion.div>
    );
}

export default function HomePage() {
    const { user, setUser, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const { currentOrg, currentOrgId, loading: orgsLoading } = useOrganization();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const token = localStorage.getItem('access_token');
    const [allSubs, setAllSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    // Keyingi (fon) yuklashlar — sahifani to'liq loader bilan almashtirmaymiz
    const [refreshing, setRefreshing] = useState(false);
    const hasLoadedRef = useRef(false);
    const [error, setError] = useState(null);

    // Sidebar state
    const [isSidebarPinned, setIsSidebarPinned] = useState(true);
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);

    const [copied, setCopied] = useState(false);
    const [expandedCats, setExpandedCats] = useState({});

    const toggleCat = (id) => setExpandedCats(p => ({ [id]: !p[id] }));

    // -- Drag to scroll references --
    const scrollRef = useRef(null);
    const isDown = useRef(false);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e) => {
        isDown.current = true;
        isDragging.current = false;
        if (scrollRef.current) {
            scrollRef.current.classList.add('cursor-grabbing');
            scrollRef.current.classList.remove('cursor-grab');
            startX.current = e.pageX - scrollRef.current.offsetLeft;
            scrollLeft.current = scrollRef.current.scrollLeft;
        }
    };

    const handleMouseLeave = () => {
        isDown.current = false;
        if (scrollRef.current) {
            scrollRef.current.classList.remove('cursor-grabbing');
            scrollRef.current.classList.add('cursor-grab');
        }
    };

    const handleMouseUp = () => {
        isDown.current = false;
        if (scrollRef.current) {
            scrollRef.current.classList.remove('cursor-grabbing');
            scrollRef.current.classList.add('cursor-grab');
        }
    };

    const handleMouseMove = (e) => {
        if (!isDown.current) return;
        e.preventDefault();

        if (scrollRef.current) {
            const x = e.pageX - scrollRef.current.offsetLeft;
            const walk = (x - startX.current) * 2; // Scroll speed multiplier

            // If moved more than 3px, consider it a drag (not a click)
            if (Math.abs(walk) > 3) {
                isDragging.current = true;
            }

            scrollRef.current.scrollLeft = scrollLeft.current - walk;
        }
    };

    // Tracking info
    const sessionTimerRef = useRef(null);
    const sessionStartTimeRef = useRef(null);

    // Selection state
    const [selectedCatId, setSelectedCatId] = useState(null);
    const [activeId, setActiveId] = useState(() => {
        return searchParams.get('dashboard') || null;
    });

    const isSidebarOpen = isSidebarPinned || isSidebarHovered;
    const showOrgRail = useShowOrgRail();
    const sidebarWidth = 280 + (showOrgRail ? ORG_RAIL_WIDTH : 0);

    // `user` obyekti favorite/token refresh'da ham yangilanadi — faqat
    // ma'lumotlar ro'yxatiga ta'sir qiladigan maydonlar o'zgarganda qayta yuklaymiz.
    const userFetchKey = user
        ? [
            user.id, user.role, user.is_superadmin ? 1 : 0, user.can_view_gantt ? 1 : 0,
            (user.gantt_projects || []).length, (user.organization_ids || []).join(','),
        ].join('|')
        : '';

    useEffect(() => {
        // Tashkilotlar hali yuklanmagan bo'lsa kutamiz (aks holda filtrsiz
        // ma'lumot ko'rinib, so'ng qayta yuklanib "miltillaydi")
        if (orgsLoading) return;
        let cancelled = false;
        if (hasLoadedRef.current) setRefreshing(true);
        else setLoading(true);
        const orgParam = currentOrgId ? { organization_id: currentOrgId } : {};
        Promise.all([
            api.get('/me/dashboards'),
            ((user?.is_superadmin || user?.can_view_gantt || user?.role === 'gantt_user') || (user?.gantt_projects && user.gantt_projects.length > 0))
                ? api.get('/gantt/projects', { params: orgParam }).catch(() => ({ data: [] }))
                : Promise.resolve({ data: [] }),
            api.get('/docs', { params: orgParam }).catch(() => ({ data: [] }))
        ])
        .then(([subsRes, ganttRes, docsRes]) => {
            if (cancelled) return;
            const normalSubs = (subsRes.data || []).filter(s => !currentOrgId || !s.organization_id || s.organization_id === currentOrgId);
            const gProjects = ganttRes.data || [];
            const documents = docsRes.data || [];
            let allMerged = [...normalSubs];

            if (gProjects && gProjects.length > 0) {
                const favId = user?.favorite_subcategory_id;
                const ganttSubs = gProjects.map((p, idx) => ({
                    id: `gantt_${p.id}`,
                    name_ru: p.name,
                    name_uz: p.name,
                    category_id: 'gantt_category',
                    category_name_ru: t('panel.project_management', 'Управление проектами'),
                    category_name_uz: t('panel.project_management', 'Loyiha boshqaruvi'),
                    category_show_icon: false,
                    category_icon_name: 'CalendarDays',
                    category_order: -1,
                    order: idx,
                    is_gantt: true,
                    gantt_project: p,
                    has_url: true,
                    is_favorite: favId === `gantt_${p.id}`
                }));
                allMerged = [...ganttSubs, ...allMerged];
            }

            // Panel sidebar: published, approved, locked, pending_approval hujjatlar
            const visibleDocs = (documents || []).filter(d =>
                ['published', 'approved', 'locked', 'pending_approval'].includes(d.status)
            );
            if (visibleDocs.length > 0) {
                const favId = user?.favorite_subcategory_id;
                const docsSubs = visibleDocs.map((d, idx) => ({
                    id: `doc_${d.id}`,
                    name_ru: d.title,
                    name_uz: d.title,
                    category_id: 'docs_category',
                    category_name_ru: t('panel.documents', 'Документы'),
                    category_name_uz: t('panel.documents', 'Hujjatlar'),
                    category_show_icon: true,
                    category_icon_name: 'FileText',
                    category_order: -2,
                    order: idx + 1,
                    is_doc: true,
                    document: d,
                    has_url: true,
                    is_favorite: favId === `doc_${d.id}`
                }));
                allMerged = [...docsSubs, ...allMerged];
            }

            setAllSubs(allMerged);

            // Initialize selection
            if (allMerged.length > 0) {
                const currentUrlId = searchParams.get('dashboard');
                let targetSub = currentUrlId ? allMerged.find(s => s.id === currentUrlId) : null;
                
                if (!targetSub) {
                    targetSub = allMerged.find(s => s.is_favorite === true);
                }
                
                if (!targetSub) {
                    const localId = localStorage.getItem('active_dashboard');
                    targetSub = allMerged.find(s => s.id === localId);
                }

                if (!targetSub) {
                    targetSub = allMerged[0];
                }

                if (targetSub) {
                    setActiveId(targetSub.id);
                    setSelectedCatId(targetSub.category_id);
                    setExpandedCats({ [targetSub.category_id]: true });
                    if (searchParams.get('dashboard') !== targetSub.id) {
                        setSearchParams({ dashboard: targetSub.id }, { replace: true });
                    }
                }
            } else {
                setActiveId(null);
                setSelectedCatId(null);
                setSearchParams({}, { replace: true });
            }
        })
        .catch(err => {
            if (cancelled) return;
            console.error("Fetch dashboards error:", err);
            setError(err.message);
        })
        .finally(() => {
            if (cancelled) return;
            hasLoadedRef.current = true;
            setLoading(false);
            setRefreshing(false);
        });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userFetchKey, currentOrgId, orgsLoading, t]);

    useEffect(() => {
        if (activeId) localStorage.setItem('active_dashboard', activeId);
        else localStorage.removeItem('active_dashboard');
    }, [activeId]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const urlId = searchParams.get('dashboard');
        if (urlId && urlId !== activeId && allSubs.some(s => s.id === urlId)) {
            setActiveId(urlId);
            const sub = allSubs.find(s => s.id === urlId);
            if (sub) setSelectedCatId(sub.category_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, allSubs]);

    // Auto-expand the relevant accordion category when a dashboard is selected
    useEffect(() => {
        if (!activeId || allSubs.length === 0) return;
        const sub = allSubs.find(s => s.id === activeId);
        if (sub) {
            setExpandedCats(prev => {
                if (prev[sub.category_id]) return prev;
                return { [sub.category_id]: true };
            });
        }
    }, [activeId, allSubs]);

    // Track activity duration accurately using visibilitychange and sendBeacon
    useEffect(() => {
        if (!activeId) return;

        let hasSent = false;
        let startTime = Date.now();

        const sendActivity = (isUnload = true) => {
            if (hasSent || !startTime) return;
            hasSent = true;

            const duration = Math.floor((Date.now() - startTime) / 1000);
            if (duration >= 3 && token) {
                if (isUnload) {
                    // Use query parameters so navigator.sendBeacon doesn't need custom headers (avoids CORS preflight)
                    const url = new URL(`${API_BASE}/panel/activity`, window.location.origin);
                    url.searchParams.append('token', token);
                    url.searchParams.append('subcategory_id', activeId);
                    url.searchParams.append('duration_seconds', duration);

                    if (navigator.sendBeacon) {
                        navigator.sendBeacon(url.toString());
                    } else {
                        fetch(url.toString(), { method: 'POST', keepalive: true }).catch(() => { });
                    }
                } else {
                    // Normal React SPA navigation: use standard API call
                    api.post('/panel/activity', {
                        subcategory_id: activeId,
                        duration_seconds: duration
                    }).catch(() => { });
                }
            }
            startTime = null; // stop timer once sent
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sendActivity(true);
            } else if (document.visibilityState === 'visible') {
                hasSent = false;
                startTime = Date.now();
            }
        };

        const handleBeforeUnload = () => {
            sendActivity(true);
        };

        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Boshqa dashboardga o'tganda odatiy API orqali jo'natish
            sendActivity(false);
        };
    }, [activeId, token]);

    const lang = i18n.language;
    const locName = (obj, ruKey, uzKey) => (lang === 'uz' ? (obj[uzKey] || obj[ruKey]) : obj[ruKey]) || '';

    const groupedCategories = useMemo(() => {
        const map = new Map();
        allSubs.forEach(s => {
            const key = s.category_id;
            if (!map.has(key)) map.set(key, {
                id: key,
                name_ru: s.category_name_ru || key,
                name_uz: s.category_name_uz || s.category_name_ru || key,
                items: [],
                show_icon: s.category_show_icon,
                icon_name: s.category_icon_name,
                icon_url: s.category_icon_url,
                order: s.category_order || 0
            });
            map.get(key).items.push(s);
        });
        return [...map.values()]
            .filter(cat => cat.items.length > 0)
            .sort((a, b) => a.order - b.order);
    }, [allSubs]);

    const currentSubcategories = useMemo(() => {
        if (!selectedCatId) return [];
        return allSubs
            .filter(s => s.category_id === selectedCatId)
            .sort((a, b) => {
                if (a.order !== b.order) return (a.order || 0) - (b.order || 0);
                return (a.name_ru || '').localeCompare(b.name_ru || '');
            });
    }, [allSubs, selectedCatId]);

    const handleDragEnd = (result) => {
        const { source, destination, type } = result;
        if (!destination) return;
        
        if (type === 'category') {
            const items = Array.from(groupedCategories);
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);
            
            const newCatIds = items.map(c => c.id);
            api.post('/me/categories/reorder', { category_ids: newCatIds }).catch(console.error);
            
            setAllSubs(prev => prev.map(s => {
                const ci = newCatIds.indexOf(s.category_id);
                return { ...s, category_order: ci !== -1 ? ci + 1 : s.category_order };
            }));
        } else if (type === 'subcategory') {
            const catId = source.droppableId;
            if (destination.droppableId !== catId) return; // only within same category
            
            const catItems = allSubs.filter(s => s.category_id === catId).sort((a,b) => a.order - b.order);
            const [reorderedItem] = catItems.splice(source.index, 1);
            catItems.splice(destination.index, 0, reorderedItem);
            
            const newSubIds = catItems.map(s => s.id);
            api.post('/me/subcategories/reorder', { subcategory_ids: newSubIds }).catch(console.error);
            
            setAllSubs(prev => prev.map(s => {
                const si = newSubIds.indexOf(s.id);
                return { ...s, order: si !== -1 ? si + 1 : s.order };
            }));
        }
    };

    const toggleFavorite = (e, subId) => {
        e.stopPropagation();
        // For document items (doc_xxx), still use the same favorite API
        // The backend stores favorite_subcategory_id as any string
        api.post('/me/dashboard/favorite', { subcategory_id: subId }).catch(console.error);
        setAllSubs(prev => prev.map(s => ({
            ...s,
            is_favorite: s.id === subId ? true : false
        })));
        setUser(prev => prev ? { ...prev, favorite_subcategory_id: subId } : prev);
    };

    const activeSub = useMemo(() =>
        allSubs.find(s => s.id === activeId),
        [allSubs, activeId]
    );

    const [timeLeft, setTimeLeft] = useState(null);
    const [visitedIds, setVisitedIds] = useState([]);
    const [refreshKeys, setRefreshKeys] = useState({});

    // Keep track of which dashboards have been mounted/visited
    useEffect(() => {
        if (activeId && !visitedIds.includes(activeId)) {
            setVisitedIds(prev => [...prev, activeId]);
        }
    }, [activeId, visitedIds]);

    // Timer effect for auto-refresh
    useEffect(() => {
        if (!activeSub || !activeSub.update_interval) {
            setTimeLeft(null);
            return;
        }
        setTimeLeft(activeSub.update_interval);

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev === null) return null;
                if (prev <= 1) {
                    setRefreshKeys(k => ({ ...k, [activeSub.id]: (k[activeSub.id] || 0) + 1 }));
                    return activeSub.update_interval;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [activeSub]);

    const formatTimeLeft = (sec) => {
        if (sec === null) return null;
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        if (h > 0) return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    function selectDashboard(id) {
        setActiveId(id);
        setSearchParams({ dashboard: id }, { replace: true });
        const sub = allSubs.find(s => s.id === id);
        if (sub) setSelectedCatId(sub.category_id);

        if (window.innerWidth < 1024) {
            setIsSidebarPinned(false);
        }
    }

    function selectCategory(catId) {
        setSelectedCatId(catId);

        if (!isSidebarPinned) {
            setIsSidebarHovered(false);
        }
    }

    function handleLogout() {
        logout();
        navigate('/login', { replace: true });
    }

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const noDashboardsImage = "/media/no_dashboards_illustration.png";

    if (loading) return (
        <div className="w-full h-screen flex items-center justify-center bg-background">
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-10 h-10 rounded-full border-4 border-brand border-t-transparent"
            />
        </div>
    );

    if (error) return (
        <div className="w-full h-screen flex items-center justify-center bg-background p-4">
            <EmptyState
                title={t('panel.error_title')}
                description={error}
                icon={AlertCircle}
            />
        </div>
    );

    return (
        <div className="flex h-screen w-full bg-surface-2 overflow-hidden text-primary">

            {/* ── SIDEBAR ── */}
            <motion.aside
                initial={false}
                animate={{
                    width: isSidebarOpen ? sidebarWidth : 0,
                }}
                transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                onMouseEnter={() => !isSidebarPinned && setIsSidebarHovered(true)}
                onMouseLeave={() => !isSidebarPinned && setIsSidebarHovered(false)}
                className={`h-full bg-surface border-border flex flex-col z-50 flex-shrink-0 overflow-hidden whitespace-nowrap
                    ${!isSidebarPinned ? 'shadow-2xl absolute left-0 top-0 bottom-0 border-r' : 'relative border-r'}
                `}
            >
                <div className="h-full flex flex-col" style={{ width: sidebarWidth }}>
                    {/* Header — butun kenglik bo'ylab (rail ustida ham) */}
                    <div className="h-16 border-b border-border flex items-center px-4 justify-between flex-shrink-0">
                        <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap">
                            <div className="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center shadow-sm overflow-hidden">
                                <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
                            </div>
                            <AnimatePresence>
                                {isSidebarOpen && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="min-w-0"
                                    >
                                        <h1 className="text-sm font-bold leading-tight">BI by CAU</h1>
                                        <CurrentOrgCaption />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {isSidebarOpen && (
                            <button
                                onClick={() => setIsSidebarPinned(!isSidebarPinned)}
                                className="p-1.5 text-muted hover:text-primary hover:bg-surface-2 rounded-md transition-colors"
                            >
                                {isSidebarPinned ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                        )}
                    </div>

                    <div className="flex-1 flex min-h-0">
                    {/* Tashkilotlar ustuni (Slack uslubi) — faqat 2+ tashkilot bo'lsa; kategoriyalar bilan bir balandlikdan boshlanadi */}
                    {showOrgRail && <OrganizationRail />}

                    <div className="w-[280px] h-full min-h-0 flex flex-col">
                    <div className="flex-1 overflow-y-auto py-4 overflow-x-hidden">
                        {/* Gantt Projects merged into categories above */}



                        <div className="px-4 mb-2 flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-wider">
                            {t('panel.categories')}
                            {refreshing && <Loader2 className="w-3 h-3 animate-spin text-brand" />}
                        </div>

                        <div className={`transition-opacity duration-200 ${refreshing ? 'opacity-60' : 'opacity-100'}`}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="categories" type="category">
                                {(provided) => (
                                    <div className="flex flex-col gap-1 px-2 pb-6" {...provided.droppableProps} ref={provided.innerRef}>
                                        {groupedCategories.map((cat, index) => {
                                            const isSelected = selectedCatId === cat.id;
                                            return (
                                                <Draggable key={cat.id} draggableId={cat.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div 
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`flex flex-col gap-1 ${snapshot.isDragging ? 'z-50' : ''}`}
                                                        >
                                                            <div className={`flex items-center gap-1 rounded-lg transition-all group relative px-1 ${isSelected ? 'bg-brand text-inverse shadow-brand-sm' : 'text-secondary hover:bg-surface-2 hover:text-primary'} ${snapshot.isDragging ? 'shadow-md ring-2 ring-brand/50' : ''}`}>
                                                                <div {...provided.dragHandleProps} className="p-1.5 cursor-grab opacity-50 hover:opacity-100 flex-shrink-0">
                                                                    <GripVertical className="w-4 h-4" />
                                                                </div>
                                                                <button
                                                                    onClick={() => {
                                                                        toggleCat(cat.id);
                                                                        selectCategory(cat.id);
                                                                    }}
                                                                    className="flex-1 flex items-center gap-2 py-2.5 overflow-hidden"
                                                                >
                                                                    {cat.show_icon && cat.icon_url ? (
                                                                        <div className="w-5 h-5 flex-shrink-0 rounded-sm bg-slate-900/5 dark:bg-white/10 flex items-center justify-center p-0.5 overflow-hidden shadow-sm">
                                                                            <img
                                                                                src={cat.icon_url.startsWith('http') ? cat.icon_url : `${API_BASE}${cat.icon_url}`}
                                                                                alt=""
                                                                                className={`w-full h-full object-contain drop-shadow-sm transition-all ${isSelected ? 'opacity-100 scale-110' : 'opacity-70 group-hover:opacity-100 group-hover:scale-105'}`}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        (() => {
                                                                            const IconComponent = Icons[cat.icon_name] || LayoutDashboard;
                                                                            return <IconComponent className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-inverse/90' : 'text-muted group-hover:text-brand'}`} />;
                                                                        })()
                                                                    )}

                                                                    <span className="text-sm font-medium truncate flex-1 text-left">
                                                                        {lang === 'en' ? (cat.name_en || cat.name_ru || cat.name_uz) : (lang === 'uz' ? (cat.name_uz || cat.name_ru) : cat.name_ru)}
                                                                    </span>
                                                                    {cat.items.some(s => s.id === activeId) && !expandedCats[cat.id] && (
                                                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 mr-1 ${isSelected ? 'bg-inverse' : 'bg-brand'}`} />
                                                                    )}
                                                                </button>
                                                                <button onClick={() => toggleCat(cat.id)} className="p-2 flex-shrink-0 opacity-50 hover:opacity-100">
                                                                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedCats[cat.id] ? '' : '-rotate-90'}`} />
                                                                </button>
                                                            </div>
                                                            <div className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${expandedCats[cat.id] ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                                                                <div className="overflow-hidden">
                                                                        <Droppable droppableId={cat.id} type="subcategory">
                                                                            {(provided) => (
                                                                                <div className="pl-6 pr-1 py-1 flex flex-col gap-0.5" {...provided.droppableProps} ref={provided.innerRef}>
                                                                                    {cat.items.sort((a,b) => a.order - b.order).map((sub, sIndex) => (
                                                                                        <Draggable key={sub.id} draggableId={sub.id} index={sIndex}>
                                                                                            {(provided, snapshot) => (
                                                                                                <div ref={provided.innerRef} {...provided.draggableProps} className={`group flex items-center justify-between px-2 py-1.5 rounded-md ${sub.id === activeId ? 'bg-brand/10 text-brand' : 'hover:bg-surface-2 text-muted'} ${snapshot.isDragging ? 'shadow-md z-50 bg-surface ring-1 ring-brand/30' : ''}`}>
                                                                                                    <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-1">
                                                                                                        <div {...provided.dragHandleProps} className="cursor-grab opacity-30 hover:opacity-100 flex-shrink-0">
                                                                                                            <GripVertical className="w-3 h-3" />
                                                                                                        </div>
                                                                                                        <div className="flex flex-col flex-1 min-w-0 cursor-pointer" onClick={() => selectDashboard(sub.id)}>
                                                                                                            <span className="truncate text-[13px] font-medium leading-tight">{lang === 'en' ? (sub.name_en || sub.name_ru || sub.name_uz) : (lang === 'uz' ? (sub.name_uz || sub.name_ru) : sub.name_ru)}</span>
                                                                                                            {sub.is_doc && sub.document?.status && (() => { const DS={draft:[t('docs.status_draft', 'Qoralama'),'text-muted/60'],published:[t('docs.status_published', 'Nashr qilingan'),'text-blue-400'],pending_approval:[t('docs.status_pending_approval', 'Tasdiq kutilmoqda'),'text-amber-400'],approved:[t('docs.status_approved', 'Tasdiqlangan'),'text-green-400'],rejected:[t('docs.status_rejected', 'Rad etilgan'),'text-red-400'],locked:[t('docs.status_locked', 'Qulflangan'),'text-purple-400']}; const st=DS[sub.document.status]; return st?<span className={`text-[10px] leading-tight truncate ${st[1]}`}>{st[0]}</span>:null; })()}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    {((!sub.is_doc && (lang === 'en' ? (sub.description_en || sub.description_ru || sub.description_uz) : (lang === 'uz' ? (sub.description_uz || sub.description_ru) : sub.description_ru))) || (sub.is_doc && sub.document?.description)) && (
                                                                                                        <div
                                                                                                            className="relative group/info flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border bg-surface border-border text-muted hover:text-brand hover:border-brand/30 hover:bg-brand/5 opacity-0 group-hover:opacity-100 transition-all cursor-help"
                                                                                                            onMouseEnter={(e) => {
                                                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                                                const tip = e.currentTarget.querySelector('[data-tip]');
                                                                                                                if (tip) { tip.style.top = `${rect.top + rect.height / 2}px`; }
                                                                                                            }}
                                                                                                        >
                                                                                                            <Icons.Info className="w-3 h-3 transition-colors" />
                                                                                                            <div data-tip className="fixed left-[282px] -translate-y-1/2 hidden group-hover/info:block z-[9999]">
                                                                                                                <div className="relative bg-surface border border-border rounded-lg shadow-xl ml-2" style={{ backdropFilter: 'blur(8px)' }}>
                                                                                                                    <div className="px-3 py-2.5 max-w-[300px] min-w-[140px] max-h-[250px] overflow-y-auto text-[11px] text-primary leading-relaxed break-words whitespace-pre-wrap custom-scrollbar">
                                                                                                                        {sub.is_doc ? sub.document.description : (lang === 'uz' ? sub.description_uz : sub.description_ru)}
                                                                                                                    </div>
                                                                                                                    <div className="absolute top-1/2 -translate-y-1/2 -left-[3px] w-2 h-2 rotate-45 bg-surface border-l border-b border-border" />
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {/* Share — faqat active bo'lganda, chapda */}
                                                                                                    {sub.id === activeId && (
                                                                                                        <button
                                                                                                            onClick={(e) => { e.stopPropagation(); handleShare(); }}
                                                                                                            className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border transition-all opacity-0 group-hover:opacity-100
                                                                                                                ${copied
                                                                                                                    ? 'bg-green-500/10 border-green-500/20 text-green-500'
                                                                                                                    : 'bg-surface border-border text-muted hover:text-brand hover:border-brand/30 hover:bg-brand/5'
                                                                                                                }`}
                                                                                                            title="Share URL"
                                                                                                        >
                                                                                                            {copied ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                                                                                                        </button>
                                                                                                    )}
                                                                                                    {/* Star — o'ngda */}
                                                                                                    <button
                                                                                                        onClick={(e) => toggleFavorite(e, sub.id)}
                                                                                                        className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border transition-all
                                                                                                            ${sub.is_favorite
                                                                                                                ? 'bg-yellow-400/10 border-yellow-400/20 text-yellow-400 opacity-100'
                                                                                                                : 'bg-surface border-border text-muted hover:text-yellow-400 hover:border-yellow-400/30 hover:bg-yellow-400/5 opacity-0 group-hover:opacity-100'
                                                                                                            }`}
                                                                                                    >
                                                                                                        <Star className={`w-3 h-3 ${sub.is_favorite ? 'fill-yellow-400' : ''}`} />
                                                                                                    </button>
                                                                                                </div>
                                                                                            )}
                                                                                        </Draggable>
                                                                                    ))}
                                                                                    {provided.placeholder}
                                                                                </div>
                                                                            )}
                                                                        </Droppable>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        </div>
                    </div>

                    <div className="p-4 border-t border-border flex-shrink-0">
                        <div className="flex flex-col gap-3">
                            {/* Language & Theme — above user card */}
                            <div className="flex items-center gap-2">
                                <div className="flex-1 flex rounded-lg overflow-hidden border border-border">
                                    {[{ code: 'ru', label: 'Рус' }, { code: 'uz', label: "O'zbek" }, { code: 'en', label: 'Eng' }].map(({ code: lang, label }) => (
                                        <button
                                            key={lang}
                                            onClick={() => i18n.changeLanguage(lang)}
                                            className={`flex-1 py-1.5 text-[10px] font-bold transition-colors ${i18n.language === lang ? 'bg-brand text-inverse' : 'text-muted hover:bg-surface-2'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={toggleTheme}
                                    className="p-2 rounded-lg border border-border text-muted hover:bg-surface-2 transition-colors flex-shrink-0"
                                    title={isDark ? t('panel.theme_light') : t('panel.theme_dark')}
                                >
                                    {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                                </motion.button>
                            </div>

                            {/* User card */}
                            <div className="flex items-center gap-3 p-2 bg-surface-2 rounded-xl border border-border">
                                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold overflow-hidden">
                                    {user?.show_photo && user?.photo ? (
                                        <img src={`${import.meta.env.VITE_API_URL || ''}${user.photo}`} alt={user.login} className="w-full h-full object-cover" />
                                    ) : (
                                        user?.last_name_ru?.charAt(0) || 'U'
                                    )}
                                </div>
                                <div className="overflow-hidden flex-1">
                                    <p className="text-sm font-bold truncate">
                                        {(user?.last_name_ru || user?.last_name_uz || user?.last_name_en)
                                            ? (() => {
                                                const l = i18n.language === 'en' ? 'en' : (i18n.language === 'uz' ? 'uz' : 'ru');
                                                const lastName = user[`last_name_${l}`] || user.last_name_en || user.last_name_ru || user.last_name_uz || '';
                                                const firstName = user[`first_name_${l}`] || user.first_name_en || user.first_name_ru || user.first_name_uz || '';
                                                const initial = getInitial(firstName);
                                                return `${lastName} ${initial}.`;
                                              })()
                                            : user?.login}
                                    </p>
                                    <p className="text-xs text-muted truncate">{user?.role === 'admin' ? t('panel.administrator') : user?.role === 'doc_user' ? 'Doc User' : user?.role === 'gantt_user' ? 'Gantt User' : t('panel.user_role')}</p>
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex gap-2">
                            {(user?.role === 'admin' || user?.role === 'gantt_user' || user?.role === 'doc_user' || user?.can_manage_gantt || user?.can_view_documents || user?.can_create_documents) && (
                                    <Link to="/admin" className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-surface-2 border border-border text-xs font-semibold hover:text-brand transition-colors">
                                        <Settings className="w-4 h-4" /> {user?.role === 'admin' ? t('panel.admin_btn') : user?.role === 'doc_user' ? 'Docs Admin' : 'Gantt Admin'}
                                    </Link>
                                )}

                                <button
                                    onClick={handleLogout}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-error/10 text-error text-xs font-semibold hover:bg-error hover:text-inverse transition-colors"
                                >
                                    <LogOut className="w-4 h-4" /> {t('panel.logout_btn')}
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                    </div>
                </div>
            </motion.aside>
            {/* ── MAIN CONTENT ── */}
            <main className="flex-1 flex flex-col h-screen min-w-0 transition-all duration-300">
                <header className="h-10 min-h-[40px] bg-surface border-b border-border flex items-center px-4 lg:px-6 z-10 gap-2">
                    {!isSidebarPinned && !isSidebarHovered && (
                        <button
                            onMouseEnter={() => setIsSidebarHovered(true)}
                            className="p-1.5 border border-border rounded-lg text-muted hover:text-brand hover:border-brand/40 hover:bg-surface-2 transition-all flex-shrink-0"
                            title={t('panel.open_menu')}
                        >
                            <LayoutDashboard className="w-4 h-4" />
                        </button>
                    )}

                    {/* Active dashboard long name — center/left */}
                    {activeSub && (
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {activeSub.is_favorite && <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" title={t('subcategories.favorite')} />}
                            <span
                                className="text-sm font-semibold text-primary truncate"
                                title={lang === 'en'
                                    ? (activeSub.long_name_en || activeSub.long_name_ru || activeSub.long_name_uz || activeSub.name_en || activeSub.name_ru || activeSub.name_uz)
                                    : (lang === 'uz'
                                        ? (activeSub.long_name_uz || activeSub.long_name_ru || activeSub.name_uz || activeSub.name_ru)
                                        : (activeSub.long_name_ru || activeSub.name_ru)
                                    )
                                }
                            >
                                {lang === 'en'
                                    ? (activeSub.long_name_en || activeSub.long_name_ru || activeSub.long_name_uz || activeSub.name_en || activeSub.name_ru || activeSub.name_uz)
                                    : (lang === 'uz'
                                        ? (activeSub.long_name_uz || activeSub.long_name_ru || activeSub.name_uz || activeSub.name_ru)
                                        : (activeSub.long_name_ru || activeSub.name_ru)
                                    )
                                }
                            </span>
                        </div>
                    )}

                    {/* Gantt Header Portal Target */}
                    {(activeId === 'gantt' || activeId?.startsWith('gantt_')) && (
                        <div id="gantt-header-portal" className="flex items-center justify-between flex-1 px-2" />
                    )}

                    {/* Right side: refresh timer */}
                    {activeSub && timeLeft !== null && (
                        <div className="ml-auto flex-shrink-0">
                            <div className="flex items-center justify-center h-7 gap-1.5 px-3 bg-brand/10 border border-brand/20 rounded-md shadow-sm" title={t('subcategories.update_interval')}>
                                <RefreshCw className={`w-3.5 h-3.5 text-brand ${timeLeft <= 3 ? 'animate-spin' : ''}`} />
                                <span className="text-[11px] font-bold text-brand tabular-nums">
                                    {formatTimeLeft(timeLeft)}
                                </span>
                            </div>
                        </div>
                    )}
                </header>

                <div className="flex-1 bg-background overflow-hidden flex flex-col">
                    {(allSubs.length === 0 && activeId !== 'gantt' && (!activeId || !activeId.startsWith('gantt_'))) ? (
                        <EmptyState
                            title={t('panel.no_dashboards')}
                            description={t('panel.no_dashboards_desc')}
                            icon={LayoutList}
                        />
                    ) : (!activeSub && activeId !== 'gantt' && (!activeId || !activeId.startsWith('gantt_'))) ? (
                        <EmptyState
                            title={t('panel.no_selected')}
                            description={t('panel.no_selected_desc')}
                            icon={LayoutDashboard}
                        />
                    ) : (activeSub && !activeSub.has_url && activeId !== 'gantt' && (!activeId || !activeId.startsWith('gantt_'))) ? (
                        <EmptyState
                            title={t('panel.dashboard_not_found')}
                            description={t('panel.dashboard_not_found_desc')}
                            icon={Monitor}
                        />
                    ) : (
                        <div className="w-full h-full relative">
                            {visitedIds.map(vId => {
                                const isVisible = activeId === vId;

                                if (vId === 'gantt' || (vId && vId.startsWith('gantt_'))) {
                                    return (
                                        <div
                                            key={vId}
                                            className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                                            aria-hidden={!isVisible}
                                        >
                                            <GanttViewerPage projectId={vId.startsWith('gantt_') ? vId.replace('gantt_', '') : null} />
                                        </div>
                                    );
                                }

                                if (vId === 'docs_list' || vId === 'docs_admin') {
                                    return (
                                        <div
                                            key={vId}
                                            className={`absolute inset-0 w-full h-full overflow-y-auto bg-background transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                                            aria-hidden={!isVisible}
                                        >
                                            <DocsListPage embedded={true} onNavigate={(path) => setSearchParams({ dashboard: path })} />
                                        </div>
                                    );
                                }

                                if (vId === 'doc_new') {
                                    return (
                                        <div
                                            key={vId}
                                            className={`absolute inset-0 w-full h-full overflow-y-auto bg-background transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                                            aria-hidden={!isVisible}
                                        >
                                            <DocEditorPage embedded={true} onNavigate={(path) => setSearchParams({ dashboard: path })} />
                                        </div>
                                    );
                                }

                                if (vId && vId.startsWith('doc_') && vId !== 'doc_new' && vId !== 'docs_list' && vId !== 'docs_admin') {
                                    const editMode = vId.endsWith('_edit');
                                    const docId = editMode ? vId.replace('doc_', '').replace('_edit', '') : vId.replace('doc_', '');
                                    
                                    if (editMode) {
                                        return (
                                            <div
                                                key={vId}
                                                className={`absolute inset-0 w-full h-full overflow-y-auto bg-background transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                                                aria-hidden={!isVisible}
                                            >
                                                <DocEditorPage docId={docId} embedded={true} onNavigate={(path) => setSearchParams({ dashboard: path })} />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={vId}
                                            className={`absolute inset-0 w-full h-full overflow-y-auto bg-background transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                                            aria-hidden={!isVisible}
                                        >
                                            <DocViewPage docId={docId} embedded={true} onNavigate={(path) => setSearchParams({ dashboard: path })} />
                                        </div>
                                    );
                                }


                                const sub = allSubs.find(s => s.id === vId);
                                if (!sub || !sub.has_url) return null;

                                return (
                                    <div
                                        key={vId}
                                        className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${isVisible ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'
                                            }`}
                                        aria-hidden={!isVisible}
                                    >
                                        <DashboardIframe
                                            key={`${vId}-${lang}-${isDark}-${refreshKeys[vId] || 0}`}
                                            sub={sub}
                                            token={token}
                                            isDark={isDark}
                                            lang={lang}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}